import { Alert } from '@warp-ds/react';
import cn from 'classnames';
import type { PropsWithChildren } from 'react';

import { SpinnerIcon } from 'shared/icons/SpinnerIcon';
import { WELCOME_TO_NEW_FHH_ON_HELPCENTER } from '@/constants';
import type { PrivateItemPrice } from '@/domain';
import { PriceDetail } from './PriceDetail';

export interface PrivatePriceOverviewProps {
    startDate: Date;
    endDate: Date;
    price?: PrivateItemPrice;
    optionalCleaningDescription: string;
    isLoading?: boolean;
    error?: Error;
    className?: string;
}

export const PrivatePriceOverview = ({
    startDate,
    endDate,
    price,
    optionalCleaningDescription = 'Leietaker velger',
    isLoading,
    error,
    className,
    children,
}: PropsWithChildren<PrivatePriceOverviewProps>) => {
    if (error)
        return (
            <Alert type="negative" show>
                Beklager, noe gikk galt ved henting av pris.
            </Alert>
        );
    if (isLoading || !price) return <SpinnerIcon />;

    const numberOfNights = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const titleWithNumberOfNights = `Pris for ${numberOfNights} ${numberOfNights > 1 ? 'netter' : 'natt'}`;

    const renderCleaning = () => {
        switch (price.cleaning.cleaningOption) {
            case 'INCLUDED':
                return (
                    <p className="grid grid-cols-2 gap-16 my-8">
                        <span className="text-left">Rengjøring</span>
                        <span className="text-right">Inkludert</span>
                    </p>
                );
            case 'FEE':
                return <PriceDetail title="Rengjøring" money={{ value: price.cleaning.amount, currency: 'NOK' }} />;
            case 'GUEST_CLEANS':
                return (
                    <p className="grid grid-cols-2 gap-16 my-8">
                        <span className="text-left">Rengjøring</span>
                        <span className="text-right">Leietaker vasker</span>
                    </p>
                );
            case 'OPTIONAL_GUEST_CLEANS_OR_FEE':
                return price.cleaning.cleaningChoice === 'FEE' ? (
                    <PriceDetail title="Rengjøring" money={{ value: price.cleaning.amount, currency: 'NOK' }} />
                ) : (
                    <p className="grid grid-cols-2 gap-16 my-8">
                        <span className="text-left">Rengjøring</span>
                        <span className="text-right">{optionalCleaningDescription}</span>
                    </p>
                );
        }
    };

    return (
        <div className={cn(className, 'text-14')}>
            {children}
            <h3 className="text-14">Pris</h3>
            <PriceDetail title={titleWithNumberOfNights} money={{ value: price.bookingValue, currency: 'NOK' }} />
            {renderCleaning()}
            {price.serviceFee && (
                <PriceDetail
                    title="FINN.no tjenestegebyr"
                    money={{ value: price.serviceFee.total.total, currency: 'NOK' }}
                    extraPriceInformation={serviceFeeInfo}
                />
            )}
            <PriceDetail title="Totalpris" money={{ value: price.total, currency: 'NOK' }} />
        </div>
    );
};

const serviceFeeInfo = {
    description: 'Tjenestegebyret gjør at vi kan fortsette å vedlikeholde og utvikle tjenestene våre. ',
    link: {
        text: 'Les mer om gebyret.',
        url: `${WELCOME_TO_NEW_FHH_ON_HELPCENTER}#FINNs-tjenestegebyr`,
    },
};
